import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { executionPlanApi } from '../services/api';
import ScheduleModal from '../components/ScheduleModal';
import Dialog from '../components/Dialog';
import { useDialog } from '../hooks/useDialog';
import Pagination from '../components/Pagination';
import './ExecutionPlanDetail.css';

/**
 * ExecutionPlanDetail Page
 * 
 * View and edit execution plan details
 */
function ExecutionPlanDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    // Edit state
    const [editedName, setEditedName] = useState('');
    const [editedDescription, setEditedDescription] = useState('');

    // Execution history state
    const [executions, setExecutions] = useState([]);
    const [executionsLoading, setExecutionsLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: parseInt(localStorage.getItem('rova_limit_plan_detail')) || 10, total: 0, totalPages: 0 });
    const { dialogState, closeDialog, confirm, alert } = useDialog();
    const [showScheduleModal, setShowScheduleModal] = useState(false);

    useEffect(() => {
        loadPlan();
        loadExecutionHistory();
    }, [id]);

    useEffect(() => {
        loadExecutionHistory();
    }, [pagination.page, pagination.limit]);

    const loadPlan = async () => {
        try {
            const response = await executionPlanApi.getExecutionPlan(id);
            const planData = response.data.executionPlan || response.data;
            setPlan(planData);
            setEditedName(planData.name);
            setEditedDescription(planData.description || '');
        } catch (err) {
            console.error('Failed to load execution plan:', err);
            setError('Failed to load execution plan');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            await executionPlanApi.updateExecutionPlan(id, {
                name: editedName,
                description: editedDescription
            });
            setIsEditing(false);
            loadPlan();
        } catch (err) {
            console.error('Failed to update plan:', err);
            alert('Failed to update execution plan');
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedName(plan.name);
        setEditedDescription(plan.description || '');
    };

    const handleExecute = async () => {
        try {
            const response = await executionPlanApi.executeExecutionPlan(id, {
                // Add execution config if needed
            });
            navigate(`/execution-plans/executions/${response.data.executionId}`);
        } catch (err) {
            console.error('Failed to execute plan:', err);
            let errorMessage = err.response?.data?.error || err.message || 'Failed to execute plan';

            let displayMessage = errorMessage;
            if (errorMessage.toLowerCase().includes('quota exceeded')) {
                displayMessage = (
                    <span>
                        {errorMessage}.{' '}
                        <Link to="/settings/billing" style={{ textDecoration: 'underline' }}>
                            Upgrade
                        </Link>
                    </span>
                );
            }

            alert({
                title: 'Error',
                message: displayMessage,
                variant: 'danger'
            });
        }
    };

    const handleDelete = async () => {
        const confirmed = await confirm({
            title: 'Delete Execution Plan',
            message: `Are you sure you want to delete "${plan.name}"? This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'danger'
        });

        if (!confirmed) return;

        try {
            await executionPlanApi.deleteExecutionPlan(id);
            navigate('/execution-plans');
        } catch (err) {
            console.error('Failed to delete plan:', err);
            await alert({
                title: 'Error',
                message: 'Failed to delete execution plan. Please try again.',
                variant: 'danger'
            });
        }
    };

    const loadExecutionHistory = async () => {
        try {
            setExecutionsLoading(true);
            const response = await executionPlanApi.getExecutionPlanHistory(id, {
                page: pagination.page,
                limit: pagination.limit
            });
            setExecutions(response.data.executions || []);
            setPagination(prev => ({
                ...prev,
                total: response.data.pagination.total,
                totalPages: response.data.pagination.totalPages
            }));
        } catch (err) {
            console.error('Failed to load execution history:', err);
        } finally {
            setExecutionsLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleLimitChange = (newLimit) => {
        setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
    };

    const formatDuration = (ms) => {
        if (!ms) return '-';
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${seconds}s`;
    };

    const getStatusBadgeClass = (status) => {
        const statusMap = {
            'success': 'success',
            'completed': 'success',
            'failed': 'failed',
            'error': 'error',
            'running': 'running',
            'pending': 'pending',
            'cancelled': 'cancelled'
        };
        return statusMap[status] || 'pending';
    };

    if (loading) {
        return (
            <div className="execution-plan-detail">
                <div className="execution-plan-detail__loading">
                    <div className="execution-plan-detail__spinner"></div>
                    <p>Loading execution plan...</p>
                </div>
            </div>
        );
    }

    if (error || !plan) {
        return (
            <div className="execution-plan-detail">
                <div className="execution-plan-detail__error">
                    <h2>Error</h2>
                    <p>{error || 'Execution plan not found'}</p>
                    <button onClick={() => navigate('/execution-plans')}>
                        ← Back to Execution Plans
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="execution-plan-detail">
            {/* Header */}
            <div className="execution-plan-detail__header">
                <button
                    className="execution-plan-detail__back-btn"
                    onClick={() => navigate('/execution-plans')}
                >
                    ← Back
                </button>
                <div className="execution-plan-detail__actions">
                    {isEditing ? (
                        <>
                            <button
                                className="execution-plan-detail__btn execution-plan-detail__btn--secondary"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                            <button
                                className="execution-plan-detail__btn execution-plan-detail__btn--primary"
                                onClick={handleSave}
                            >
                                Save Changes
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className="execution-plan-detail__btn execution-plan-detail__btn--secondary"
                                onClick={() => navigate(`/execution-plans/${id}/edit`)}
                            >
                                Edit
                            </button>
                            <button
                                className="execution-plan-detail__btn execution-plan-detail__btn--primary"
                                onClick={handleExecute}
                            >
                                Execute
                            </button>
                            <button
                                className="execution-plan-detail__btn execution-plan-detail__btn--danger"
                                onClick={handleDelete}
                            >
                                Delete
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="execution-plan-detail__content">
                {/* Plan Info */}
                <div className="execution-plan-detail__card">
                    <h2>Plan Information</h2>

                    {isEditing ? (
                        <div className="execution-plan-detail__form">
                            <div className="execution-plan-detail__form-group">
                                <label>Plan Name</label>
                                <input
                                    type="text"
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.target.value)}
                                    className="execution-plan-detail__input"
                                />
                            </div>
                            <div className="execution-plan-detail__form-group">
                                <label>Description</label>
                                <textarea
                                    value={editedDescription}
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    className="execution-plan-detail__textarea"
                                    rows={4}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="execution-plan-detail__info">
                            <div className="execution-plan-detail__info-row">
                                <span className="execution-plan-detail__label">Name:</span>
                                <span className="execution-plan-detail__value">{plan.name}</span>
                            </div>
                            {plan.description && (
                                <div className="execution-plan-detail__info-row">
                                    <span className="execution-plan-detail__label">Description:</span>
                                    <span className="execution-plan-detail__value">{plan.description}</span>
                                </div>
                            )}
                            <div className="execution-plan-detail__info-row">
                                <span className="execution-plan-detail__label">Execution Mode:</span>
                                <span className="execution-plan-detail__value">{plan.executionMode}</span>
                            </div>
                            <div className="execution-plan-detail__info-row">
                                <span className="execution-plan-detail__label">Platform:</span>
                                <span className="execution-plan-detail__value">{plan.platform}</span>
                            </div>
                            <div className="execution-plan-detail__info-row">
                                <span className="execution-plan-detail__label">Created:</span>
                                <span className="execution-plan-detail__value">
                                    {new Date(plan.createdAt).toLocaleString()}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Test Goals */}
                <div className="execution-plan-detail__card">
                    <h2>Test Goals ({plan.testGoals?.length || 0})</h2>
                    {plan.testGoals && plan.testGoals.length > 0 ? (
                        <div className="execution-plan-detail__goals">
                            {[...plan.testGoals]
                                .sort((a, b) => (a.ExecutionPlanGoals?.order || 0) - (b.ExecutionPlanGoals?.order || 0))
                                .map((goal, index) => (
                                    <div key={goal.id} className="execution-plan-detail__goal">
                                        <div className="execution-plan-detail__goal-header">
                                            <span className="execution-plan-detail__goal-number">
                                                #{goal.ExecutionPlanGoals?.order || index + 1}
                                            </span>
                                            <span className="execution-plan-detail__goal-text">
                                                {goal.goalText}
                                            </span>
                                        </div>
                                        {goal.priority && (
                                            <span className={`execution-plan-detail__badge execution-plan-detail__badge--${goal.priority}`}>
                                                {goal.priority}
                                            </span>
                                        )}
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <p className="execution-plan-detail__empty">No test goals defined</p>
                    )}
                </div>

                {/* Execution History */}
                <div className="execution-plan-detail__card">
                    <h2>Execution History</h2>
                    {executionsLoading ? (
                        <div className="execution-plan-detail__loading">Loading executions...</div>
                    ) : executions.length > 0 ? (
                        <>
                            <div className="execution-plan-detail__history-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Status</th>
                                            <th>Goals</th>
                                            <th>Duration</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {executions.map((execution) => (
                                            <tr key={execution.id}>
                                                <td>
                                                    {new Date(execution.createdAt).toLocaleString()}
                                                </td>
                                                <td>
                                                    <span className={`execution-plan-detail__status-badge execution-plan-detail__status-badge--${getStatusBadgeClass(execution.status)}`}>
                                                        {execution.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="execution-plan-detail__goal-counts">
                                                        <span className="execution-plan-detail__goal-count execution-plan-detail__goal-count--passed">
                                                            ✓ {execution.passedGoals || 0}
                                                        </span>
                                                        <span className="execution-plan-detail__goal-count execution-plan-detail__goal-count--failed">
                                                            ✗ {execution.failedGoals || 0}
                                                        </span>
                                                        {execution.skippedGoals > 0 && (
                                                            <span className="execution-plan-detail__goal-count execution-plan-detail__goal-count--skipped">
                                                                ⊘ {execution.skippedGoals}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{formatDuration(execution.duration)}</td>
                                                <td>
                                                    <Link
                                                        to={`/execution-plans/executions/${execution.id}`}
                                                        className="execution-plan-detail__view-btn"
                                                    >
                                                        View Report
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                page={pagination.page}
                                totalPages={pagination.totalPages}
                                limit={pagination.limit}
                                onPageChange={handlePageChange}
                                onLimitChange={handleLimitChange}
                                storageKey="rova_limit_plan_detail"
                            />
                        </>
                    ) : (
                        <p className="execution-plan-detail__empty">No executions yet. Execute this plan to see history.</p>
                    )}
                </div>
            </div>

            <Dialog {...dialogState} onClose={closeDialog} />

            {showScheduleModal && (
                <ScheduleModal
                    onClose={() => setShowScheduleModal(false)}
                    onComplete={() => {
                        setShowScheduleModal(false);
                        alert({
                            title: 'Success',
                            message: 'Schedule created/updated successfully',
                            variant: 'success'
                        });
                    }}
                    preselectedGoals={plan.testGoals || []}
                    initialData={{
                        name: `${plan.name} Schedule`,
                        description: `Schedule for execution plan: ${plan.name}`
                    }}
                />
            )}
        </div>
    );
}

export default ExecutionPlanDetail;
